/* eslint-disable no-underscore-dangle */
/* eslint-disable import/prefer-default-export */
import React from "react";

/* ----------------------------------
   STATUS BADGE
----------------------------------- */
export const getStatusBadge = (status) => {
  if (!status) return null;

  const value = status.toLowerCase();

  if (value.includes("exact")) {
    return <span className="badge badge-red">Exact Duplicate</span>;
  }

  if (value.includes("partial")) {
    return <span className="badge badge-yellow">Partial Duplicate</span>;
  }

  return <span className="badge badge-blue">Match Source</span>;
};

const MatchDot = ({ type }) => {
  if (!type) return null;
  if (type === "full") return <span className="dot red" />;
  if (type === "partial") return <span className="dot yellow" />;
  return null;
};

/* ----------------------------------
   DUPLICATE CONFIG
----------------------------------- */
export const duplicateConfig = {
  /* ---------------- PAGE CONFIG ---------------- */
  agentName: "Intake Review Agent",
  title: "Duplicate Report",
  subtitle: "Intake Review Agent",

  /* ---------------- TABLE DATA ---------------- */
  staticData: [
    {
      caseId: "",
      dataSource: "",
      submitDate: "",
      origin: "",
      firstName: "",
      lastName: "",
      caseManager: "",
      updatedAt: "",
      notes: "",
    },
  ],

  /* ---------------- TABLE COLUMNS ---------------- */
  /**
   * NOTE:
   * Columns are built dynamically so that
   * "Data Source" appears ONLY for Benefits Agent
   */
  getColumns: (args = {}) => {
    const isBenefitsAgent = typeof args === "boolean" ? args : args.isBenefitsAgent;

    const showAction = typeof args === "object" ? args.showAction !== false : true;

    const columns = [
      {
        header: "Case ID",
        accessor: "caseId",
        render: (value, row, _, onRowSelect) => (
          <div className="caseid-with-checkbox">
            <input
              type="checkbox"
              checked={onRowSelect?.selected?.some(
                (r) => r.caseId === row.caseId,
              )}
              onChange={() => onRowSelect(row)}
            />
            <span>{value}</span>
          </div>
        ),
      },
    ];

    //  Benefits Agent ONLY
    // if (isBenefitsAgent) {
    //   columns.push({
    //     header: "Data Source",
    //     accessor: "dataSource",
    //     render: (v) => <span className="pill blue">{v || "-"}</span>,
    //   });
    // }

    columns.push(
      { header: "Case Submit Date", accessor: "submitDate" },
      { header: "Intake Origin", accessor: "origin" },

      {
        header: "First Name",
        accessor: "firstName",
        render: (value, row) => {
          const matchType = row?._compare?.firstName?.toLowerCase() ?? null;

          if (isBenefitsAgent) return <span>{value}</span>;

          if (matchType !== "full" && matchType !== "partial") return <span>{value}</span>;

          return (
            <div className="name-with-icon">
              <MatchDot type={matchType} />
              <span>{value}</span>
            </div>
          );
        },
      },

      {
        header: "Last Name",
        accessor: "lastName",
        render: (value, row) => (isBenefitsAgent ? (
          <span>{value}</span>
        ) : (
          <div className="name-with-icon">
            <MatchDot type={row?._compare?.lastName} />
            <span>{value}</span>
          </div>
        )),
      },

      { header: "Case Manager", accessor: "caseManager" },

      // FIXED DATA RENDER
      {
        header: "Case Manager Updated",
        accessor: "caseManagerUpdated",
        render: (v) => (v ? (
          <>
            <div className="primary-text">{v.date}</div>
            <div className="secondary-text">{v.time}</div>
          </>
        ) : (
          "-"
        )),
      },

      { header: "Notes", accessor: "notes" },
      { header: "Email Address", accessor: "email" },
      { header: "Street Address", accessor: "mailStreet" },
      { header: "City", accessor: "mailCity" },
      { header: "State", accessor: "mailState" },
      { header: "County", accessor: "mailCounty" },
      { header: "Zip", accessor: "mailZip" },
      { header: "Property Street", accessor: "propStreet" },
      { header: "Property City", accessor: "propCity" },
      { header: "Property State", accessor: "propState" },
      { header: "Property County", accessor: "propCounty" },
      { header: "Property Zip", accessor: "propZip" },

    );

    // Intake Review ONLY
    if (!isBenefitsAgent) {
      columns.push({
        header: "Status",
        accessor: "status",
      });
    }

    if (showAction) {
      columns.push({
        header: "Action",
        accessor: "action",
        render: (_, row, onTagPrimary) => (
          <button
            type="button"
            onClick={() => onTagPrimary(row)}
            className="tagAsPrimary-btn"
          >
            Tag as Primary
          </button>
        ),
      });
    }

    return columns;
  },

  /* ---------------- PRIMARY APPLICATION TABLE ---------------- */
  primaryApplColumns: [
    { header: "Case ID", accessor: "caseId" },
    { header: "Case Submit Date", accessor: "submitDate" },
    {
      header: "Intake Origin",
      accessor: "origin",
      render: (v) => <span>{v}</span>,
    },
    {
      header: "First Name",
      accessor: "firstName",
      render: (v) => (
        <span>
          <span className="dot blue" />
          {' '}
          {v}
        </span>
      ),
    },
    {
      header: "Last Name",
      accessor: "lastName",
      render: (v) => (
        <span>
          <span className="dot blue" />
          {' '}
          {v}
        </span>
      ),
    },
    {
      header: "Case Manager",
      accessor: "caseManager",
      render: (v) => <span>{v}</span>,
    },
    {
      header: "Case Manager Updated",
      accessor: "caseManagerUpdated",
      render: (v) => (
        <>
          <div className="primary-text">{v?.date}</div>
          <div className="secondary-text">{v?.time}</div>
        </>
      ),
    },
    {
      header: "Notes",
      accessor: "notes",
    },
  ],

  /* ---------------- PRIMARY APPLICATION DATA ---------------- */
  primaryApplData: [
    {
      caseId: "2028",
      submitDate: "Aug 8, 2024",
      origin: "Website App",
      firstName: "Sharma",
      lastName: "G. Manopavan",
      caseManager: "Assigned",
      caseManagerUpdated: {
        date: "Dec 11, 2025",
        time: "11:34 PM",
      },
      notes: "98% Complete",

      details: [
        {
          title: "Statuses",
          fields: {
            intakeStatus: "Completed",
            intakeStatusUpdated: "Dec 11, 2025",
            activityStatus: "In-progress",
            activityStatusUpdated: "Dec 11, 2025",
            activityStatusDetails: "Waiting for approval",
            workflowStatus: "Active",
            workflowStatusUpdated: "Dec 11, 2025",
          },
        },
        {
          title: "Contact Details",
          fields: {
            primaryPhone: "123-456-7890",
            secondaryPhone: "987-654-3210",
            email: "user@email.com",
          },
        },
        {
          title: "Personal Details",
          fields: {
            dob: "12 Aug 1990",
            gender: "Male",
            race: "Asian",
            ethnicity: "Indian",
          },
        },
        {
          title: "Mailing Address",
          fields: {
            mailStreet: "123 Main St",
            mailCity: "New York",
            mailState: "NY",
            mailCountry: "USA",
            mailZip: "10001",
          },
        },
      ],
    },
  ],
};
